import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { IContact, IDropdown } from "../../models/BaseModel";
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useEffect, useState } from "react";
import { Fetch } from "../../shared/Fetch";
import { JVInput } from "../../shared/JVInput";
import { JVDropdown } from "../../shared/JVDropdown";
import { InputMask } from "primereact/inputmask";
import { classNames } from "primereact/utils";
import { JVInputMask } from "../../shared/JVInputMask";
import { InputTextarea } from "primereact/inputtextarea";

const schema = yup.object({
    name: yup.string().trim().required("Name is required"),
    phoneNumber: yup.string().required("PhoneNumber is required"),
    countryId: yup.number().typeError("Country is required").required("Country is required").min(1, "Country is required"),
    stateId: yup.number().nullable(),
    cityId: yup.number().nullable(),
    email: yup.string().email().nullable(),
    whatsapp: yup.string().nullable(),
    remark: yup.string().nullable(),
    alternativeEmail: yup.string().email("Invalid Alternative Email").nullable(),
     alternativePhone: yup.string().nullable(),
});

interface Iprops {
    singleData: IContact,
    open: boolean,
    hideDialog: () => void,
    toast: any,
    reloadData: () => void,
    hasPermission: (pageName: string, featureName?: string) => boolean
}

export const Form = (props: Iprops) => {
    const [countries, setCountries] = useState<IDropdown[]>();
    const [sameAsContact, setSameAsContact] = useState(false);
    const [states, setStates] = useState<IDropdown[]>();
    const [cities, setCities] = useState<IDropdown[]>();
    const [submitting, setSubmitting] = useState(false);

    const { control, handleSubmit, watch, setValue, formState: { errors }, reset } = useForm<IContact>({
        resolver: yupResolver(schema),
        defaultValues: props.singleData,
        shouldFocusError: false,
    });

    const countryId = watch("countryId");
    const stateId = watch("stateId");

    useEffect(() => {
        loadCountries();
    }, []);
    useEffect(() => {
        if (countryId) {
            if (!props.singleData.id) {
                setValue("stateId", undefined);
                setValue("cityId", undefined);
            }
            setStates([]);
            setCities([]);
            loadStates(countryId);
        }
    }, [countryId]);

    useEffect(() => {
        if (stateId) {
            if (!props.singleData.id) {
                setValue("cityId", undefined);
            }
            setCities([]);
            loadCities(stateId);
        }
    }, [stateId]);

    useEffect(() => {
        setSameAsContact(false);

        if (props.singleData?.id) {
            let rawPhone = props.singleData.phoneNumber || "";
            let rawWhatsapp = props.singleData.whatsapp || ""
            let dial = props.singleData.dialCode || "";
            rawPhone = rawPhone.replace(/\s+/g, "");
            rawWhatsapp = rawWhatsapp.replace(/\s+/g, "");

            let phone = rawPhone;
            let wp = rawWhatsapp;
            if (dial && rawPhone.startsWith(dial)) {
                phone = rawPhone.substring(dial.length);
            }
            if (dial && rawWhatsapp.startsWith(dial)) {
                wp = rawWhatsapp.substring(dial.length);
            }

            reset({
                ...props.singleData,
                phoneNumber: phone,
                whatsapp: wp
            });
        } else {
            reset(props.singleData);
        }
    }, [props.singleData, reset]);

    const loadCountries = async () => {
        const response = await Fetch("country/dropdown", "GET", null, null, null);
        if (response) {
            setCountries(await response.json());
        }
    }

    const loadStates = async (countryId: number) => {
        const response = await Fetch(`state/dropdown/${countryId}`, "GET", null, null, null);
        if (response) {
            setStates(await response.json());
        }
    }

    const loadCities = async (stateId: number) => {
        const response = await Fetch(`city/dropdown/${stateId}`, "GET", null, null, null);
        if (response) {
            setCities(await response.json());
        }
    }

  const saveToDB = async (body: IContact) => {
    setSubmitting(true);

    const method = props.singleData.id ? "PUT" : "POST";
    const response = await Fetch(
        `contact${props.singleData.id ? "/" + props.singleData.id : ""}`,
        method,
        body,
        null,
        props.toast
    );

    if (response) {
        props.reloadData();      // reload table first
        props.hideDialog();      // close dialog
        reset({});               // clear form

        props.toast.current.show({
            severity: "success",
            detail: props.singleData.id ? "Updated Successfully" : "Added Successfully",
            life: 3000
        });
    }

    setSubmitting(false);
};

    const onHide = () => {
        props.hideDialog();
        reset({});
    }

    function onSubmit(data: IContact) {
        saveToDB(data);
    }

    const contact = watch("phoneNumber");

    useEffect(() => {
        if (sameAsContact && !props.singleData.id) {
            setValue("whatsapp", contact);
        }
    }, [contact]);

    function onSameAsContact() {
        if (sameAsContact) {
            setValue("whatsapp", undefined);
            setSameAsContact(false);
        } else {
            setValue("whatsapp", contact);
            setSameAsContact(true);
        }
    }

    return (
        <Dialog visible={props.open} style={{ width: '80vw' }} header="Add/Edit Contact" modal className="p-fluid" onHide={onHide}>
            <form onSubmit={handleSubmit(onSubmit)} className="p-fluid formgrid grid">
                
                <JVInput column={3} required label="Full Name" control={control} name="name" error={errors.name?.message} />
                <JVInputMask column={3} required label="Contact" mask="9999999999" control={control} name="phoneNumber" error={errors.phoneNumber?.message} />
                <JVInput column={3} label="Email" control={control} name="email" error={errors.email?.message} />

                <div className="field col-12 md:col-4">
    <label htmlFor="whatsapp">
        Whatsapp
    </label>

    <Controller
        name="whatsapp"
        control={control}
        render={({ field, fieldState }) => (
            <InputMask
                {...field}
                mask="9999999999"
                className={classNames("w-full", {
                    "p-invalid": fieldState.invalid
                })}
            />
        )}
    />

    <div className="flex align-items-center mt-2">
        <input
            type="checkbox"
            id="sameAsContact"
            checked={sameAsContact}
            onChange={onSameAsContact}
        />
        <label htmlFor="sameAsContact" className="ml-2 mb-0">
            Same as Contact
        </label>
    </div>

    {errors.whatsapp && (
        <small className="p-error">{errors.whatsapp.message}</small>
    )}
</div>

                <JVInput column={3} label="Alternative Email" control={control} name="alternativeEmail" error={errors.alternativeEmail?.message} />
                <JVInputMask column={3} label="Alternative Contact" mask="9999999999" control={control} name="alternativePhone" error={errors.alternativePhone?.message} />


                <JVDropdown column={3} required label="Country" control={control} options={countries} optionLabel="name" optionValue="id" name="countryId" placeholder="Select Country" error={errors.countryId?.message} />
                <JVDropdown column={3} label="State" control={control} options={states} optionLabel="name" optionValue="id" name="stateId" placeholder="Select State" error={errors.stateId?.message} />
                <JVDropdown column={3} label="City" control={control} options={cities} optionLabel="name" optionValue="id" name="cityId" placeholder="Select City" error={errors.cityId?.message} />

                <div className="field col-12">
    <label htmlFor="remark">Remark / Description</label>

    <Controller
        name="remark"
        control={control}
        render={({ field, fieldState }) => (
            <InputTextarea
                id="remark"
                {...field}
                rows={4}
                autoResize
                placeholder="Enter remark or description"
                className={classNames({
                    "p-invalid": fieldState.invalid
                })}
            />
        )}
    />

    {errors.remark && (
        <small className="p-error">
            {errors.remark.message}
        </small>
    )}
                </div>

                <div className="col-12 md:col-12 flex gap-4 justify-content-end">
                    <Button style={{ width: '100px' }} loading={submitting} label="SAVE" type="submit" className="p-button-rounded"></Button>
                    <Button style={{ width: '100px' }} label="CLOSE" onClick={onHide} type="button" className="p-button-rounded p-button-outlined surface-200"></Button>
                </div>
            </form>
        </Dialog>
    );
}